import { Checkbox } from '@mantine/core'
import { expectedLitres, formatClock } from '@racedash/shared'
import type { FuelBurn, FuelStretch, Refuel } from '@racedash/shared'
import type { EventFuelContext } from '../../dashboard/useFuelBurn'

/**
 * What one car burns, across the event.
 *
 * The rates on top, then every stretch from one full tank to the next, oldest
 * first, with what it measured and what the rates say it should have taken —
 * which is how a stretch that does not belong stands out. Whoever may say who
 * is in the car may also say a fill was not to the top, or leave a stretch
 * out; everyone else sees what they decided.
 */
export function BurnView({
  car,
  burn,
  loading,
  error,
  fuel,
}: {
  car: string
  burn: FuelBurn | null
  loading: boolean
  error: string | null
  fuel: EventFuelContext
}) {
  if (error) return <div className="fuel-screen-empty">Cannot read the fuel: {error}</div>
  if (!burn) return <div className="fuel-screen-empty">{loading ? 'Loading…' : 'No fills for this car.'}</div>
  if (burn.stretches.length === 0) {
    return <div className="fuel-screen-empty">Car {car} has not been refuelled at this event yet.</div>
  }

  const { rates, open } = burn
  const edit = fuel.mark
  const used = rates.green != null ? expectedLitres(open, rates) : null

  return (
    <div className="fuel-screen-scroll">
      <div className="burn-rates">
        <Rate label="Green" value={rates.green} />
        <Rate label="Under FCY" value={rates.fcy} />
        <div className="burn-basis">
          From {rates.stretches} stretch{rates.stretches === 1 ? '' : 'es'}, {rates.laps} laps
          {rates.fcyLaps >= 0.5 ? `, ${laps(rates.fcyLaps)} under yellow` : ''}.
          {rates.note && <span className="burn-note"> {rates.note}.</span>}
        </div>
      </div>

      <p className="burn-open">
        In the car{open.since ? ` since ${when(open.since)}` : ''}: {open.laps} lap{open.laps === 1 ? '' : 's'}
        {open.fcyLaps >= 0.5 ? `, ${laps(open.fcyLaps)} under yellow` : ''}
        {used != null && open.laps > 0 ? ` — about ${(used - open.shortLitres).toFixed(1)} L to fill` : ''}
        {open.shortLitres > 0 ? ` (after ${open.shortLitres.toFixed(1)} L of short fill)` : ''}.
      </p>

      <table className="fuel-table burn-table">
        <thead>
          <tr>
            <th>Fuel in</th>
            <th className="num">Laps</th>
            <th className="num">FCY</th>
            <th className="num">Litres</th>
            <th className="num">L/lap</th>
            <th className="num">Expected</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {burn.stretches.map((s) => (
            <StretchRow key={s.key} s={s} burn={burn} mark={edit} />
          ))}
        </tbody>
      </table>
      <p className="fuel-screen-note">
        Each row is from one full tank to the next, across sessions. The first is from the start of the
        event, with the car full. Untick “to the top” for a fill that was not, and its litres are
        added to the next. FCY is laps under a full-course yellow, by sector.
      </p>
    </div>
  )
}

function StretchRow({ s, burn, mark }: { s: FuelStretch; burn: FuelBurn; mark: EventFuelContext['mark'] }) {
  const expected = s.laps > 0 ? expectedLitres(s, burn.rates) : null
  const off = expected != null && expected > 0 ? (s.litres - expected) / expected : null
  const ids = s.refuels.flatMap((r) => r.fills.map((f) => f.id))
  return (
    <tr className={s.excluded || s.laps === 0 ? 'burn-out' : undefined}>
      <td>
        {s.refuels.map((r) => (
          <RefuelLine key={r.fills[0]!.id} r={r} mark={mark} />
        ))}
      </td>
      <td className="num mono">
        {s.laps}
        {s.fromStart ? '*' : ''}
      </td>
      <td className="num mono">{s.fcyLaps >= 0.5 ? laps(s.fcyLaps) : '—'}</td>
      <td className="num mono">{s.litres.toFixed(2)}</td>
      <td className="num mono">{s.rate != null ? s.rate.toFixed(2) : '—'}</td>
      <td className={`num mono${off != null && Math.abs(off) > 0.08 ? ' burn-off' : ''}`}>
        {expected != null ? `${expected.toFixed(1)}${off != null ? ` (${off > 0 ? '+' : ''}${Math.round(off * 100)}%)` : ''}` : '—'}
      </td>
      <td>
        {s.laps === 0 ? (
          <span className="fuel-table-muted">no laps</span>
        ) : (
          <Checkbox
            size="xs"
            checked={!s.excluded}
            disabled={!mark}
            aria-label="Count this stretch in the rate"
            onChange={(e) => void mark?.(ids, { excluded: !e.currentTarget.checked })}
          />
        )}
      </td>
    </tr>
  )
}

/** One refuel within a stretch: when, how much, and whether it filled the tank. */
function RefuelLine({ r, mark }: { r: Refuel; mark: EventFuelContext['mark'] }) {
  const ids = r.fills.map((f) => f.id)
  return (
    <div className="burn-refuel">
      <span className="mono">{when(r.at)}</span>
      <span className="mono">{r.litres.toFixed(1)} L</span>
      {mark ? (
        <Checkbox
          size="xs"
          label="to the top"
          checked={!r.partial}
          onChange={(e) => void mark(ids, { partial: !e.currentTarget.checked })}
        />
      ) : (
        r.partial && <span className="fuel-table-muted">short fill</span>
      )}
    </div>
  )
}

function Rate({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="burn-rate">
      <span className="burn-rate-label">{label}</span>
      <span className="burn-rate-value mono">{value != null ? value.toFixed(2) : '—'}</span>
      <span className="burn-rate-unit">L/lap</span>
    </div>
  )
}

function laps(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

/** A weekday as well as a time: an event runs over days. */
function when(d: Date): string {
  return `${d.toLocaleDateString(undefined, { weekday: 'short' })} ${formatClock(d)}`
}
